import { useEffect, useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { authApi } from '@/api/auth';
import { refreshSession } from '@/api/client';
import { useAuthStore } from '@/lib/auth-store';
import { clearCoachClientState } from '@/components/coach/coach-client-state';
import { clearUserAiQueryCache } from '@/lib/ai-query-cache';
import type { LoginRequest, TelegramLoginRequest, UserCreate, UserRead } from '@/api/types';

export function useAuth() {
  const access = useAuthStore((s) => s.accessToken);
  const hasSession = useAuthStore((s) => s.hasSession);
  const setAccessToken = useAuthStore((s) => s.setAccessToken);
  const clear = useAuthStore((s) => s.clear);
  const qc = useQueryClient();
  const nav = useNavigate();
  const [isBooting, setIsBooting] = useState<boolean>(hasSession && !access);

  useEffect(() => {
    let cancelled = false;
    async function boot() {
      if (!hasSession || access) { setIsBooting(false); return; }
      await refreshSession();
      if (!cancelled) setIsBooting(false);
    }
    boot();
    return () => { cancelled = true; };
  }, [hasSession, access]);

  const userQuery = useQuery<UserRead>({
    queryKey: ['me'],
    queryFn: authApi.me,
    enabled: !!access,
    staleTime: 60_000,
  });

  const loginMut = useMutation({
    mutationFn: (body: LoginRequest) => authApi.login(body),
    onSuccess: (res) => {
      setAccessToken(res.access_token.access_token);
      qc.invalidateQueries({ queryKey: ['me'] });
    },
  });

  const telegramMut = useMutation({
    mutationFn: (body: TelegramLoginRequest) => authApi.telegramLogin(body),
    onSuccess: (res) => {
      setAccessToken(res.access_token.access_token);
      qc.invalidateQueries({ queryKey: ['me'] });
    },
  });

  const registerMut = useMutation({
    mutationFn: (body: UserCreate) => authApi.register(body),
  });

  const logout = useCallback(() => {
    const userId = userQuery.data?.id ?? qc.getQueryData<UserRead>(['me'])?.id;
    clearUserAiQueryCache(qc, userId);
    clearCoachClientState(userId);
    void authApi.logout().catch(() => undefined);
    clear();
    qc.clear();
    nav('/login', { replace: true });
  }, [clear, nav, qc, userQuery.data?.id]);

  return {
    user: userQuery.data ?? null,
    isUserLoading: userQuery.isPending,
    isUserError: userQuery.isError,
    refetchUser: userQuery.refetch,
    isAuthed: !!access,
    isBooting,
    loginMut, telegramMut, registerMut, logout,
  };
}
